import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { LayoutDashboard, Users2, UserCog, ScrollText, PlayCircle, Mail, FileBadge, BarChart3, BookOpen, Trash2, Layers } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, testId: "nav-admin-dashboard" },
  { to: "/admin/executives", label: "Marketing Executives", icon: UserCog, testId: "nav-admin-executives" },
  { to: "/admin/students", label: "Students", icon: Users2, testId: "nav-admin-students" },
  { to: "/admin/inbox", label: "Inbox", icon: Mail, testId: "nav-admin-inbox" },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText, testId: "nav-admin-audit" },
  { to: "/admin/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-admin-sample-report" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, testId: "nav-admin-analytics" },
  { to: "/admin/how-it-works", label: "How It Works", icon: BookOpen, testId: "nav-admin-how-it-works" },
  { to: "/admin/questions", label: "Question Bank", icon: Layers, testId: "nav-admin-questions" },
  { to: "/me/demo", label: "Demo Mode", icon: PlayCircle, testId: "nav-admin-demo" },
];

export default function AdminInbox() {
  const [messages, setMessages] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const load = () => api.get("/admin/inbox").then((res) => setMessages(res.data));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.post(`/admin/inbox/${id}/read`);
    load();
  };

  const confirmDelete = async () => {
    try {
      await api.post(`/admin/inbox/${deleteTarget}/delete`);
      toast.success("Message deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete message");
    }
    setDeleteTarget(null);
    load();
  };

  return (
    <DashboardShell links={LINKS} activePath="/admin/inbox">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6" data-testid="admin-inbox-heading">Inbox</h1>
      <div className="space-y-3">
        {messages.map((m) => (
          <div key={m.message_id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`inbox-message-${m.message_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-medium text-slate-900">{m.subject}</span>
                  {m.status === "NEW" && <Badge className="bg-indigo-600">New</Badge>}
                  {m.marketing_executive_id && <Badge variant="secondary">Referred by {m.marketing_executive_id}</Badge>}
                </div>
                <p className="text-xs text-slate-400">{m.name} · {m.email} · {new Date(m.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                {m.status === "NEW" && (
                  <Button size="sm" variant="outline" data-testid={`mark-read-button-${m.message_id}`} onClick={() => markRead(m.message_id)}>Mark as Read</Button>
                )}
                <Button size="icon" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`admin-delete-message-button-${m.message_id}`} onClick={() => setDeleteTarget(m.message_id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <p className="text-sm text-slate-600 mt-3">{m.message}</p>
          </div>
        ))}
        {messages.length === 0 && <p className="text-slate-400 text-sm" data-testid="admin-inbox-empty">No messages yet</p>}
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-message-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message permanently?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-message-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="delete-message-confirm-button" onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}
